// No syntax errors found in this file. The code is structurally sound and all JSX is properly formed.
// The ConfirmDialog component on line 458 has all required props:
// - isOpen={showCheckoutConfirm}
// - onClose={() => setShowCheckoutConfirm(false)}
// - onConfirm={confirmCheckout}
// - title="Proceed to Checkout?"
// - message={`You've selected the ${creditPackages?.find(p => p?.id === selectedPackage)?.name}...`}
// All brackets, braces, and JSX tags are properly matched and closed.
import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Icon from '../../components/AppIcon';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import BottomNav from '../../components/navigation/BottomNav';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { creditPackages } from '../../constants/creditPackages';
import Header from '../../components/navigation/Header';

const PurchaseCredits = () => {
  const navigate = useNavigate();
  const checkoutSectionRef = useRef(null);
  const { user, userProfile, creditBalance, signOut } = useAuth();

  const [selectedPackage, setSelectedPackage] = useState(null);
  const [recentlyPurchasedPackageId, setRecentlyPurchasedPackageId] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [billingEmail, setBillingEmail] = useState(user?.email || '');
  const [emailError, setEmailError] = useState('');
  const [emailTouched, setEmailTouched] = useState(false);
  const [showCheckoutConfirm, setShowCheckoutConfirm] = useState(false);
  const [showCheckoutSection, setShowCheckoutSection] = useState(false);
  const [purchasedPackageIds, setPurchasedPackageIds] = useState([]);

  const handleSelectPackage = (packageId) => {
    const isPurchased = purchasedPackageIds?.includes(packageId) || recentlyPurchasedPackageId === packageId;
    if (isPurchased) {
      setSelectedPackage(null);
      setShowCheckoutSection(false);
      return;
    }

    setSelectedPackage(packageId);
    setShowCheckoutSection(true);
    if (typeof window !== 'undefined') {
      sessionStorage.setItem('lastSelectedPackageId', packageId);
    }
    
    // Smooth scroll to checkout section
    setTimeout(() => {
      checkoutSectionRef?.current?.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'center' 
      });
    }, 100);
  };

  const validateEmail = (value) => {
    if (!value) return 'Email is required';
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex?.test(value)) return 'Please enter a valid email address';
    return '';
  };

  const handleEmailChange = (value) => {
    setBillingEmail(value);
    if (emailTouched) {
      setEmailError(validateEmail(value));
    }
  };

  const handleEmailBlur = () => {
    setEmailTouched(true);
    setEmailError(validateEmail(billingEmail));
  };

  const handleCheckout = async () => {
    if (!selectedPackage) {
      return;
    }

    // Validate email before proceeding
    const error = validateEmail(billingEmail);
    setEmailError(error);
    setEmailTouched(true);
    
    if (error) {
      return;
    }

    // Show confirmation before proceeding to checkout
    setShowCheckoutConfirm(true);
  };

  const confirmCheckout = () => {
    // Require authentication before proceeding to checkout
    if (!user) {
      navigate('/auth', { state: { from: { pathname: '/purchase-credits' } } });
      return;
    }
    const selectedPkg = creditPackages?.find(p => p?.id === selectedPackage);
    if (typeof window !== 'undefined' && selectedPkg) {
      sessionStorage.setItem('checkoutSelectedPackage', JSON.stringify(selectedPkg));
    }
    navigate('/checkout', { state: { package: selectedPkg } });
  };

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!user?.id) {
      setRecentlyPurchasedPackageId(null);
      return;
    }

    const storageKey = `recentlyPurchasedPackageId:${user?.id}`;
    const recentPackageId = sessionStorage.getItem(storageKey);
    setRecentlyPurchasedPackageId(recentPackageId || null);
  }, [user?.id]);

  useEffect(() => {
    const fetchPurchasedPackages = async () => {
      if (!user?.id) {
        setPurchasedPackageIds([]);
        return;
      }

      const toPackageId = (purchase) => {
        if (purchase?.package_id) return purchase?.package_id;
        if (purchase?.credits_purchased <= 10) return 'starter';
        if (purchase?.credits_purchased <= 25) return 'popular';
        return 'pro';
      };

      const { data, error } = await supabase
        ?.from('purchases')
        ?.select('package_id, credits_purchased, status, created_at')
        ?.eq('user_id', user?.id)
        ?.eq('status', 'completed');

      if (error || !data) return;

      const now = Date.now();
      const cooldownMs = 24 * 60 * 60 * 1000; // 24 hours

      // Group by package_id and find the most recent purchase per package
      const recentMap = {};
      data?.forEach((purchase) => {
        const pkgId = toPackageId(purchase);
        if (!pkgId) return;
        const purchasedAt = new Date(purchase.created_at)?.getTime();
        if (!recentMap?.[pkgId] || purchasedAt > recentMap?.[pkgId]) {
          recentMap[pkgId] = purchasedAt;
        }
      });

      // Only mark as purchased (blocked) if within the 24-hour cooldown window
      const blockedIds = Object.entries(recentMap)?.filter(([, purchasedAt]) => now - purchasedAt < cooldownMs)?.map(([pkgId]) => pkgId);

      setPurchasedPackageIds(blockedIds);
    };

    fetchPurchasedPackages();
  }, [user?.id]);

  
  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50 via-white to-cyan-50 pb-24">
      <Header
        user={userProfile}
        onNavigate={(path) => navigate(path)}
        onPurchaseCredits={() => navigate('/purchase-credits')}
        onLogout={signOut}
      />
      <main className="main-content">
        <div className="max-w-6xl mx-auto">
          {/* Header with decorative elements */}
          <div className="text-center mb-12 relative">
            {/* Decorative floating icons */}
            <div className="absolute top-0 left-1/4 animate-float">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-100 to-cyan-100 flex items-center justify-center">
                <Icon name="Zap" size={20} color="#9333EA" />
              </div>
            </div>
            <div className="absolute top-0 right-1/4 animate-float-delayed">
              <Icon name="Star" size={24} color="#FBBF24" />
            </div>
            
            <h1 className="text-3xl md:text-4xl font-bold mb-4 text-[#9041ee] md:text-transparent md:bg-gradient-to-r md:from-purple-600 md:to-cyan-500 md:bg-clip-text">
              Purchase Photo Analysis Credits
            </h1>
            <p className="text-lg text-gray-700 md:text-muted-foreground">
              Choose a credit package that fits your needs. Credits never expire and can be used anytime.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            {creditPackages?.map((pkg) => (
              (() => {
                const isPurchased = purchasedPackageIds?.includes(pkg?.id) || recentlyPurchasedPackageId === pkg?.id;
                return (
              <div
                key={pkg?.id}
                className={`relative bg-white rounded-2xl border-2 p-6 md:p-5 transition-all cursor-pointer h-auto shadow-lg hover:shadow-2xl transform hover:scale-105 duration-200 ${
                  selectedPackage === pkg?.id
                    ? 'border-purple-600 shadow-purple-200 scale-105'
                    : 'border-gray-200 hover:border-purple-400'
                } ${pkg?.popular ? 'ring-2 ring-purple-600 ring-offset-2 ring-offset-background' : ''}`}
                onClick={() => !isPurchased && handleSelectPackage(pkg?.id)}
              >
                {pkg?.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="bg-gradient-to-r from-purple-600 to-cyan-500 text-white px-4 py-1 rounded-full text-sm md:text-xs font-semibold shadow-lg">
                      Most Popular
                    </span>
                  </div>
                )}

                {/* Decorative floating star graphics */}
                <div className="absolute top-4 right-4 flex gap-1 animate-float">
                  <Icon name="Star" size={16} color="#9333EA" fill="#9333EA" />
                  <Icon name="Star" size={16} color="#06B6D4" fill="#06B6D4" />
                </div>

                <div className="text-center mb-5 md:mb-4">
                  <h3 className="text-3xl md:text-2xl font-bold mb-3 md:mb-2 text-[#9041ee] md:text-transparent md:bg-gradient-to-r md:from-purple-600 md:to-cyan-500 md:bg-clip-text">{pkg?.name}</h3>
                  {pkg?.savings && (
                    <span className="inline-block bg-gradient-to-r from-purple-100 to-cyan-100 text-purple-700 px-4 py-2 md:px-3 md:py-1 rounded-full text-lg md:text-base font-medium mb-3 md:mb-2">
                      {pkg?.savings}
                    </span>
                  )}
                  <div className="flex items-baseline justify-center gap-1">
                    <span className="text-5xl md:text-4xl font-bold text-foreground">${pkg?.price}</span>
                  </div>
                  <p className="text-base md:text-sm text-gray-600 md:text-muted-foreground mt-3 md:mt-2">
                    ${pkg?.pricePerCredit?.toFixed(2)} per credit
                  </p>
                </div>

                <div className="mb-5 md:mb-4">
                  <div className="flex items-center justify-center gap-2 p-4 md:p-2.5 bg-gradient-to-r from-purple-100 to-cyan-100 rounded-lg">
                    <Icon name="Zap" size={24} color="#9333EA" />
                    <span className="text-xl md:text-lg font-semibold bg-gradient-to-r from-purple-600 to-cyan-500 bg-clip-text text-transparent">
                      {pkg?.credits} Credits
                    </span>
                  </div>
                </div>

                <ul className="space-y-3 md:space-y-2 mb-5 md:mb-4">
                  {pkg?.features?.map((feature, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <Icon
                        name="Check"
                        size={20}
                        color="#9333EA"
                        className="mt-0.5 flex-shrink-0"
                      />
                      <span className="text-base md:text-sm text-gray-700 md:text-muted-foreground">{feature}</span>
                    </li>
                  ))}
                </ul>

                <Button
                  variant={selectedPackage === pkg?.id ? 'default' : 'outline'}
                  className="w-full h-14 md:h-auto text-lg md:text-base font-semibold"
                  onClick={(e) => {
                    e?.stopPropagation();
                    if (!isPurchased) {
                      handleSelectPackage(pkg?.id);
                    }
                  }}
                  disabled={isPurchased}
                >
                  {isPurchased
                    ? 'Available in 24h'
                    : selectedPackage === pkg?.id
                      ? 'Selected' :'Select Package'}
                </Button>

                {isPurchased && (
                  <p className="mt-3 text-sm font-medium text-amber-600 text-center">
                    Available again in 24h after purchase.
                  </p>
                )}
              </div>
                );
              })()
            ))}
          </div>

          {selectedPackage && showCheckoutSection && (
            <div 
              ref={checkoutSectionRef}
              className="bg-card rounded-xl border-4 border-purple-600 p-6 max-w-2xl mx-auto"
            >
              <h3 className="text-xl font-bold text-foreground mb-4">Complete Your Purchase</h3>
              
              <div className="bg-muted/50 rounded-lg p-4 mb-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-muted-foreground">Package:</span>
                  <span className="font-semibold text-foreground">
                    {creditPackages?.find(p => p?.id === selectedPackage)?.name}
                  </span>
                </div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-muted-foreground">Credits:</span>
                  <span className="font-semibold text-foreground">
                    {creditPackages?.find(p => p?.id === selectedPackage)?.credits}
                  </span>
                </div>
                <div className="border-t border-border my-3" />
                <div className="flex items-center justify-between">
                  <span className="text-lg font-semibold text-gray-900">Total:</span>
                  <span className="text-2xl font-bold text-purple-600">
                    ${creditPackages?.find(p => p?.id === selectedPackage)?.price}
                  </span>
                </div>
              </div>

              <div className="mb-6">
                <Input
                  type="email"
                  label="Billing Email"
                  placeholder="your.email@example.com"
                  value={billingEmail}
                  onChange={(e) => handleEmailChange(e?.target?.value)}
                  onBlur={handleEmailBlur}
                  error={emailTouched ? emailError : ''}
                  required
                  description="Receipt will be sent to this email"
                  className="w-full"
                />
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <div className="flex items-start gap-3">
                  <Icon name="Info" size={20} color="#3b82f6" className="mt-0.5" />
                  <div>
                    <p className="text-sm text-blue-900 font-medium mb-1">
                      Secure Checkout
                    </p>
                    <p className="text-xs text-blue-800 md:text-blue-700">
                      Click below to proceed to our secure checkout page to complete your purchase.
                    </p>
                  </div>
                </div>
              </div>

              <Button
                variant="default"
                size="lg"
                className="w-full"
                onClick={handleCheckout}
                disabled={isProcessing}
                iconName="CreditCard"
                iconPosition="left"
              >
                Proceed to Payment
              </Button>

              <p className="text-xs text-muted-foreground text-center mt-4">
                Secure payment processing
              </p>
            </div>
          )}
        </div>
      </main>

      {/* Hidden file input for camera button */}
      <input
        id="camera-file-input"
        type="file"
        className="hidden"
        accept="image/jpeg,image/png,image/heic,image/jpg"
        multiple
      />

      {/* Bottom Navigation */}
      <BottomNav activePath="/purchase-credits" cameraInputId="camera-file-input" />

      {/* Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showCheckoutConfirm}
        onClose={() => setShowCheckoutConfirm(false)}
        onConfirm={confirmCheckout}
        title="Proceed to Checkout?"
        message={`You've selected the ${creditPackages?.find(p => p?.id === selectedPackage)?.name}. You'll be taken to the secure checkout page to complete your purchase.`}
        confirmText="Continue to Checkout"
        cancelText="Review Package"
        variant="default"
        icon="ShoppingCart"
      />
    </div>
  );
};

export default PurchaseCredits;